import { ChangeDetectionStrategy, Component, computed, inject, OnInit, Signal, ViewEncapsulation } from '@angular/core';
import { ITodoItem, ITodoSection } from '../../fi-todo';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiTodoHelperService } from '../../fi-todo-helper.service';
import { FiTodoListStoreService } from '../fi-todo-list-editor-store.service';
import { FiTooltipIconClassType } from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button.component';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';

@Component({
    selector: 'fi-todo-sections-edit',
    templateUrl: './fi-todo-sections-edit.component.html',
    styleUrls: ['./fi-todo-sections-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-todo-sections-edit' },
    standalone: false,
})
export class FiTodoSectionsEditComponent implements OnInit {
    private fiContentLanguageService = inject(FiContentLanguageSharedService);
    private todoHelperService = inject(FiTodoHelperService);
    private todoListStoreService = inject(FiTodoListStoreService);
    private saveChangesWarningService = inject(SaveChangesWarningService);

    protected todoListSections: Signal<ITodoSection[]> = this.todoListStoreService.currentTodoListSections;
    protected selectedLanguageId: Signal<number> = this.todoListStoreService.selectedLanguageId;

    protected getNewTodoSectionInstance = (): ITodoSection => {
        return this.todoHelperService.getNewTodoSectionInstance();
    };
    protected readonly availableContentLanguages = this.fiContentLanguageService.getContentLanguages();

    protected isDeleteSectionButtonDisabledFn = (section: ITodoSection): boolean => {
        return section.Items.some((item) => item.HasResponses);
    };

    public ngOnInit(): void {
        this.startWarningChanges();
    }

    private startWarningChanges(): void {
        this.saveChangesWarningService.startForMultipleObjects([
            (isReceivingInitialObject) =>
                isReceivingInitialObject
                    ? this.todoListStoreService.initialTodoListData()
                    : this.todoListStoreService.currentTodoListData(),
            (isReceivingInitialObject) =>
                isReceivingInitialObject
                    ? this.todoListStoreService.currentTodoListData().Sections
                    : this.todoListSections(),
        ]);
        this.saveChangesWarningService.preventParentStateObjCheck = true;
        this.saveChangesWarningService.parentStateObjIndexes = [0];
    }

    protected navigateToEditTodoItem(sectionItem: ITodoItem, todoItemIndex: number, todoSectionIndex: number): void {
        this.saveChangesWarningService.stop();
        this.todoListStoreService.setCurrentTodoListItem(sectionItem);
        this.todoListStoreService.setCurrentTodoListItemIndex(todoItemIndex);
        this.todoListStoreService.setCurrentTodoListSectionIndex(todoSectionIndex);
        this.todoHelperService.navigateToTodoItemEditor();
    }

    protected confirmTodoItemRemove(todoItem: ITodoItem, sectionIndex: number, todoListItemIndex: number): void {
        this.todoHelperService.confirmTodoItemRemove(todoItem, sectionIndex, todoListItemIndex);
    }

    protected confirmTodoSectionRemove(sectionIndex: number): void {
        this.todoHelperService.confirmTodoSectionRemove(sectionIndex);
    }

    protected copyTodoItem(sectionIndex: number, todoListItemIndex: number): void {
        this.todoHelperService.copyTodoItem(sectionIndex, todoListItemIndex);
    }

    protected openAddTodoItemEditorDialog(sectionIndex: number = 0): void {
        this.todoHelperService.openAddTodoItemDialog(sectionIndex);
    }

    protected setSelectedLanguageId(langId: number): void {
        this.todoListStoreService.setSelectedLanguageId(langId);
    }

    protected onSectionsChange(sections: ITodoSection[]): void {
        this.todoHelperService.onSectionsDataChanged(sections);
    }

    protected navigateToTodoEdit(): void {
        this.todoHelperService.navigateToTodoListEdit();
    }

    protected saveTodoListSections(): void {
        this.todoListStoreService.updateTodoListSectionsOnTodoListData(this.todoListSections());
        this.saveChangesWarningService.stop();
        this.navigateToTodoEdit();
    }

    protected readonly FiTooltipIconClassType = FiTooltipIconClassType;
    protected readonly ObjectTypes = ObjectTypes;
}
